import type { FC } from 'react';
import { useCallback, useState } from 'react';

import type { LoginCredentials } from '@opendoor/auth-fe';
import { Col, Form, Text, TextInput } from '@opendoor/bricks-next';
import { GoogleAuth } from '@opendoor/cinderblocks/platform/auth/GoogleAuth';
import { Link } from 'platform/bricks-next';
import { COSMOS_DOMAIN, GOOGLE_CLIENT_ID } from 'shared/helpers/globals';
import { useLazyAsyncFn } from 'shared/hooks/useLazyAsyncFn';

import { useAuthAuthenticated } from './AuthContext';

interface ReauthFormProps {
  onSuccess: () => void;
}

/** UI for the user to reauthenticate. Commonly used before allowing the user to view sensitive data. */
const ReauthFormWithoutContext: FC<
  ReauthFormProps & Pick<ReturnType<typeof useAuthAuthenticated>, 'user' | 'verifyLogin'>
> = ({ onSuccess, user, verifyLogin }) => {
  const [verifyLoginLazy, verifyLoginResult] = useLazyAsyncFn(verifyLogin);

  const verifyLoginCallback = useCallback(
    async (credentials: LoginCredentials) => {
      const result = await verifyLoginLazy(credentials);

      if (result.success) {
        onSuccess();
      } // errors handled with loginLastCall
    },
    [verifyLoginLazy, onSuccess],
  );

  const [password, setPassword] = useState('');

  const loginFailed = verifyLoginResult.data && !verifyLoginResult.data.success;

  return (
    <Col gap="$12x">
      <Text tag="p" typography="$bodyMedium">
        Signing in as{' '}
        <Link
          variant="secondary"
          size="medium"
          analyticsName="user-email"
          href={`mailto:${user.email}`}
        >
          {user.email}
        </Link>
      </Text>
      <Form
        formId="cake-reauth-form"
        onSubmit={(event) => {
          event.preventDefault();
          void verifyLoginCallback({ email: user.email, password });
        }}
      >
        <Form.Form>
          <Col gap="$12x">
            <TextInput
              analyticsName="cake-reauthform-password"
              id="reauth-password"
              size="medium"
              onChangeText={(value) => setPassword(value || '')}
              required
              textContentType="password"
              title="Password"
              errorText={
                loginFailed
                  ? 'Incorrect email or password. Use the link below to reset your password'
                  : ''
              }
            />
            <Form.Button
              variant="primary"
              size="medium"
              analyticsName="reauth-submit"
              label="Confirm"
              aria-label="Confirm and submit password"
              loading={verifyLoginResult?.loading}
              disabled={verifyLoginResult?.loading || password.length === 0}
            />
          </Col>
        </Form.Form>
      </Form>
      <GoogleAuth
        clientId={GOOGLE_CLIENT_ID}
        onAuthed={(response) => void verifyLoginCallback({ googleIdToken: response.tokenId })}
        buttonSize="medium"
      />
      <Col alignItems="center">
        <Link
          variant="primary"
          size="medium"
          href={`${COSMOS_DOMAIN}/reset-password`}
          target="_blank"
          rel="noopener noreferrer"
          analyticsName="re-auth-forgot-password"
          aria-label="Opens the forgot password page"
        >
          Forgot your password?
        </Link>
      </Col>
    </Col>
  );
};

export const ReauthForm: FC<ReauthFormProps> = ({ onSuccess }) => {
  const { user, verifyLogin } = useAuthAuthenticated();

  return <ReauthFormWithoutContext onSuccess={onSuccess} user={user} verifyLogin={verifyLogin} />;
};
